import { useState, memo, useEffect, FC } from 'react';
import { ReactNodeLike } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { noop, isNil, isEmpty } from 'lodash';
// Import Context
import { useSubmitSubscription } from 'context';
// Import Types
import type { TitleKeyProps } from 'types';
// Import UI components
import { Icon, ExtendedInput, SkeletonLoader } from '@geneui/components';
// Import Components
import PreSaveValidationModal from 'components/common/Modals/PreSaveValidationModal';
import PageHeadlineActionBar from './PageHeadlineActionBar';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
import { useValidator } from 'hooks';
// Import Enums
import { SaveModesEnum } from 'enums';
// Import Constants
import { l, ModalsActions } from 'constants/common';
import { redirectMetaData } from './config';
// Import Styles
import 'assets/scss/pageHeadline.scss';

interface Props extends TitleKeyProps {
    isDataReady?: boolean;
    defaultName?: Nilable<string>;
    defaultDescription?: Nullable<string>;
    isLoading?: boolean;
    handleReset?: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleSave?: ((...args: any[]) => Promise<any>) | ((...args: any[]) => void);
    handleTestSend?: () => void;
    handleNameChange?: (name: Nullable<string>) => void;
    handleDescriptionChange?: (description: Nullable<string>) => void;
    validator?: (data: { Name?: Nullable<string>; Description?: Nullable<string> }) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additionalSettings?: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getCustomValidationTypeErrorText?: any;
    doValidation?: (mode: SaveModesEnum) => boolean;
    doCustomValidation?: (mode?: SaveModesEnum) => boolean;
    ignoredFields?: string[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleSaved?: (data: any) => void;
    preSaveModalContent?: ReactNodeLike;
    isValidPreSaveModalContent?: boolean;
    isViewPage?: boolean;
    isTitleAndDescriptionEditable?: boolean;
    isVisibleTitleInModal?: boolean;
    isVisibleDescriptionInModal?: boolean;
    isVisibleTitleAndDescriptionFields?: boolean;
    preSaveModalAlertContent?: ReactNodeLike;
    withWarningsAlertContent?: boolean;
    visibleLeavePageOption?: boolean;
    preSaveInfoLabel?: Nullable<string>;
    shouldNotUsePageValidation?: boolean;
    shouldDoNotLeavePage?: boolean;
    additionalValidationProperties?: Record<string, AnyObject>[];
}

const PageHeadline: FC<Props> = ({
    titleKey,
    isDataReady = null, // TODO: should be change default value isDataReady null -> false
    defaultName = null,
    defaultDescription = null,
    isLoading = false,
    handleReset = noop,
    handleSave = noop,
    handleSaved = noop,
    handleNameChange = noop,
    handleDescriptionChange = noop,
    handleTestSend,
    additionalSettings = {},
    getCustomValidationTypeErrorText,
    doValidation = () => true,
    doCustomValidation,
    ignoredFields,
    preSaveModalContent,
    isValidPreSaveModalContent = true,
    isViewPage = false,
    isTitleAndDescriptionEditable = true,
    isVisibleTitleInModal = true,
    isVisibleDescriptionInModal = true,
    isVisibleTitleAndDescriptionFields = true,
    preSaveModalAlertContent,
    withWarningsAlertContent = false,
    visibleLeavePageOption = true,
    preSaveInfoLabel,
    shouldNotUsePageValidation,
    shouldDoNotLeavePage,
    additionalValidationProperties = [],
    ...rest
}) => {
    const { t } = useTranslation();
    const { triggerSubmission } = useSubmitSubscription();
    const { goToPreviousRoute } = useGoToRoute();
    const validator = useValidator(getCustomValidationTypeErrorText, ignoredFields);

    const [isPreSaveModalOpen, setPreSaveModalOpen] = useState(false);

    const [name, setName] = useState(defaultName);
    const [isVisibleLeavePageOption, setIsVisibleLeavePageOption] = useState(false); // TODO: remove this when in all pages is visible leave page option
    const [description, setDescription] = useState(defaultDescription);
    const [saveButtonLabel, setSaveButtonLabel] = useState(l.SaveAsDraft);

    useEffect(() => {
        // TODO temp solution for reset and leave page
        setIsVisibleLeavePageOption(
            titleKey.includes('ExclusionRulesManagement') ||
                titleKey.includes('Report') ||
                titleKey.endsWith('CustomerJourney') ||
                titleKey.includes('DynamicSegment') ||
                titleKey.includes('ConvertToReport') ||
                titleKey.includes('CompositeSegment') ||
                titleKey.includes('NotificationCampaign') ||
                titleKey.includes('CustomerJourneyVisualization') ||
                titleKey.includes('FormulaFilter') ||
                (titleKey.includes('ActionCampaign') && visibleLeavePageOption) ||
                (titleKey.includes('SMS Provider') && visibleLeavePageOption) ||
                titleKey.includes('Achievement') ||
                titleKey.includes('Article'),
        );
    }, [titleKey, visibleLeavePageOption]);

    useEffect(() => {
        setName(defaultName);
        handleNameChange(defaultName);
        setDescription(defaultDescription);
        handleDescriptionChange(defaultDescription);
        validator({ Name: defaultName, Description: defaultDescription });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultName, defaultDescription]);

    const backButtonClickHandler = () => {
        goToPreviousRoute();
    };

    const nameChangeHandler = (e, value) => {
        const name = !isNil(value) ? value : e?.target?.value?.trimStart();
        const nameValue = !isEmpty(name) ? name : null;
        setName(nameValue);
        validator({ Name: nameValue });
        handleNameChange(nameValue);
    };

    const descriptionChangeHandler = (e, value) => {
        const description = !isNil(value) ? value : e?.target?.value?.trimStart();
        const descriptionValue = !isEmpty(description) ? description : null;
        setDescription(descriptionValue);
        validator({ Description: descriptionValue });
        handleDescriptionChange(descriptionValue);
    };

    const nameBlurHandler = (e) => {
        nameChangeHandler(null, e.target.value.trimEnd());
    };

    const descriptionBlurHandler = (e) => {
        descriptionChangeHandler(null, e.target.value.trimEnd());
    };

    const resetHandler = () => {
        // TODO: ART temp solution for reset, I think we must delete the null case section
        if (isVisibleLeavePageOption) {
            setName(defaultName);
            handleNameChange(defaultName);
            setDescription(defaultDescription);
            handleDescriptionChange(defaultDescription);
        } else {
            if (isTitleAndDescriptionEditable) {
                setName(null);
                handleNameChange(null);
                setDescription(null);
                handleDescriptionChange(null);
            }
        }
        handleReset();
    };

    const onPreSavePopUpCancel = (action, data) => {
        if (action === ModalsActions.SAVE) {
            handleSaved(data);
        }
        setPreSaveModalOpen(false);
    };

    const saveButtonHandler = (mode: SaveModesEnum) => {
        const isAdditionalValid =
            isEmpty(additionalValidationProperties) ||
            additionalValidationProperties.some((property) => !validator(property));

        triggerSubmission();

        const isPageInvalid = doCustomValidation ? doCustomValidation(mode) : doValidation(mode) ? validator() : false;

        if (!isPageInvalid && isAdditionalValid && !isLoading && isDataReady !== false) {
            setPreSaveModalOpen(true);
        }
    };

    const testSendHandler = () => {
        const isPageValid = doCustomValidation ? doCustomValidation() : false;

        if (!isPageValid && !isLoading && isDataReady !== false) {
            handleTestSend?.();
        }
    };

    const saveHandler = (pageName, pageDescription, leavePage) => {
        // TODO: need reset existing data from page.

        const buttonWithDropDownConfig = {
            SaveAsDraft: handleSave,
            ...additionalSettings,
        };

        return buttonWithDropDownConfig[saveButtonLabel](pageName, pageDescription, saveButtonLabel, leavePage);
    };

    return (
        <div className="crm-page-headline">
            {isPreSaveModalOpen && !isViewPage && (
                <PreSaveValidationModal
                    defaultName={name || ''}
                    defaultDescription={description || ''}
                    title={redirectMetaData[titleKey]?.title}
                    route={redirectMetaData[titleKey]?.route}
                    pageName={t(redirectMetaData[titleKey]?.pageName)}
                    onCancel={onPreSavePopUpCancel}
                    handleDescriptionChange={descriptionChangeHandler}
                    handleNameChange={nameChangeHandler}
                    onOk={saveHandler}
                    saveButtonLabel={saveButtonLabel}
                    bodyContent={preSaveModalContent}
                    isValidBodyContent={isValidPreSaveModalContent}
                    isVisibleLeavePageOption={isVisibleLeavePageOption}
                    isVisibleTitleInModal={isVisibleTitleInModal}
                    isVisibleDescriptionInModal={isVisibleDescriptionInModal}
                    alertContent={preSaveModalAlertContent}
                    withWarningsAlertContent={withWarningsAlertContent}
                    infoLabel={preSaveInfoLabel}
                    shouldNotUsePageValidation={!isVisibleTitleAndDescriptionFields}
                    shouldDoNotLeavePage={shouldDoNotLeavePage}
                />
            )}
            <div className="crm-page-headline_back-button-part">
                <Icon type="bc-icon-arrow-back" onClick={backButtonClickHandler} />
            </div>

            <div className="crm-page-headline_page-name-part crm-page-headline-name-col">
                <p>{t(titleKey)}</p>
            </div>

            <div className="crm-page-headline_content-part">
                {!isViewPage && (
                    <>
                        <div className="page-h-left">
                            {isVisibleTitleAndDescriptionFields && (
                                <div>
                                    <SkeletonLoader isBusy={isLoading} height="20px" width="150px">
                                        <ExtendedInput
                                            className="crm-page-headline_description"
                                            inputSize="small"
                                            placeholder={t(l.Untitled)}
                                            defaultValue={defaultName}
                                            value={name}
                                            onChange={nameChangeHandler}
                                            onBlur={nameBlurHandler}
                                            appearance="outline"
                                            readOnly={!isTitleAndDescriptionEditable}
                                            tooltipText={!isTitleAndDescriptionEditable && name ? name : undefined}
                                        />
                                    </SkeletonLoader>
                                    <SkeletonLoader isBusy={isLoading} height="20px" width="100px">
                                        <ExtendedInput
                                            className="crm-page-headline_name"
                                            inputSize="small"
                                            placeholder={t(l.TypeDescription)}
                                            defaultValue={defaultDescription}
                                            value={description}
                                            onBlur={descriptionBlurHandler}
                                            onChange={descriptionChangeHandler}
                                            readOnly={!isTitleAndDescriptionEditable}
                                            tooltipText={
                                                !isTitleAndDescriptionEditable && description ? description : undefined
                                            }
                                        />
                                    </SkeletonLoader>
                                </div>
                            )}
                        </div>
                        <div className="page-h-right">
                            <PageHeadlineActionBar
                                handleReset={resetHandler}
                                handleSave={saveButtonHandler}
                                handleTestSend={handleTestSend ? testSendHandler : undefined}
                                setSaveButtonLabel={setSaveButtonLabel}
                                hasAdditionalSettings={!isEmpty(additionalSettings)}
                                buttonWithDropDownConfig={additionalSettings}
                                disableSave={isNil(isDataReady) ? false : !isDataReady}
                                disableReset={isNil(isDataReady) ? false : !isDataReady}
                                {...rest}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default memo(PageHeadline);
